import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/base/Breadcrumb";
import { reviews } from "@/mocks/reviews";

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem("website_alt") as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormStatus("success");
      form.reset();
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    setIsSubmitting(true);
    setFormError("");

    try {
      const formData = new FormData(form);
      formData.delete("website_alt");
      const response = await fetch("https://readdy.ai/api/form/d9pdpt2qs38sj6jc7me0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> = {};
      try { parsed = JSON.parse(responseText); } catch { /* ignore */ }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        form.reset();
        setTimeout(() => setFormStatus("idle"), 3000);
      } else {
        const msg = (parsed?.meta as Record<string, string>)?.message
          || (parsed?.meta as Record<string, string>)?.detail
          || (parsed?.message as string)
          || responseText
          || "提交失败，请稍后重试";
        setFormError(typeof msg === "string" ? msg : "提交失败，请稍后重试");
        setFormStatus("error");
      }
    } catch {
      setFormError("网络异常，请稍后重试");
      setFormStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "Organization",
              name: "4399漫画网",
              url: "https://www.4399mh.com",
              description: "4399漫画网提供无删减韩国漫画下拉式阅读，免费韩漫大全资源齐全多样",
              contactPoint: {
                "@type": "ContactPoint",
                email: "contact@4399mh.com",
                contactType: "customer service",
                availableLanguage: ["Chinese"],
              },
            },
            {
              "@context": "https://schema.org",
              "@type": "WebPage",
              name: "关于我们 - 4399漫画网",
              description: "了解4399漫画网的品牌故事、用户口碑和联系方式",
              url: "https://www.4399mh.com/about",
              isPartOf: { "@type": "WebSite", name: "4399漫画网", url: "https://www.4399mh.com" },
            },
            {
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: [
                { "@type": "Question", name: "4399漫画网上的韩漫都是免费的吗？", acceptedAnswer: { "@type": "Answer", text: "是的，4399漫画网上的所有韩国漫画资源均为免费阅读，无需付费即可畅享完整的韩漫内容。" } },
                { "@type": "Question", name: "漫画更新频率是怎样的？", acceptedAnswer: { "@type": "Answer", text: "我们与韩国漫画工作室保持同步更新，大部分连载作品每周至少更新一话，热门作品可能会更频繁。" } },
                { "@type": "Question", name: "支持哪些设备阅读？", acceptedAnswer: { "@type": "Answer", text: "4399漫画网全面适配PC、平板、手机等各类设备，采用响应式设计，在任何设备上都能获得流畅的阅读体验。" } },
                { "@type": "Question", name: "如何搜索我想看的漫画？", acceptedAnswer: { "@type": "Answer", text: "您可以通过网站顶部导航栏的搜索框输入漫画名称或关键词进行搜索，也可以通过分类页面按类型浏览。" } },
                { "@type": "Question", name: "漫画内容有删减吗？", acceptedAnswer: { "@type": "Answer", text: "我们坚持提供无删减版本，尊重原作的完整性。所有漫画内容均获得正版授权，完整呈现作者原意。" } },
              ],
            },
          ]),
        }}
      />
      <Navbar />
      <main className="pt-16 min-h-screen">
        {/* Hero */}
        <section className="bg-background-50 border-b border-background-200/70">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: "关于我们" }]} />
            <div className="pb-8 md:pb-12">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
                关于4399漫画网
              </h1>
              <p className="text-base md:text-lg text-foreground-600 max-w-3xl leading-relaxed">
                4399漫画网是国内领先的韩国漫画在线阅读平台，致力于为广大韩漫爱好者提供
                无删减、高质量的下拉式韩漫阅读体验。我们拥有齐全多样的免费韩漫资源，涵盖
                恋爱、奇幻、动作、悬疑等多个类型，让每一位读者都能在这里找到心仪的作品。
              </p>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {[
                { value: "2000+", label: "漫画作品", icon: "ri-book-open-line" },
                { value: "50万+", label: "活跃用户", icon: "ri-user-heart-line" },
                { value: "99.9%", label: "服务稳定性", icon: "ri-server-line" },
                { value: "10万+", label: "日更新章节", icon: "ri-file-copy-2-line" },
              ].map((stat) => (
                <div key={stat.label} className="p-5 rounded-xl bg-background-100 text-center border border-background-200/60">
                  <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mx-auto mb-3">
                    <i className={`${stat.icon} text-xl text-primary-500`}></i>
                  </div>
                  <p className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">{stat.value}</p>
                  <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Story */}
        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
                品牌故事
              </h2>
              <div className="space-y-4 text-foreground-600 leading-relaxed text-sm md:text-base">
                <p>
                  4399漫画网创立于2020年，创始团队由一群热爱韩国漫画的资深漫迷组成。我们发现，当时国内缺少一个真正专注于韩国漫画、
                  提供优质阅读体验的平台。于是，我们决定亲手打造一个让韩漫爱好者满意的家。
                </p>
                <p>
                  经过多年的发展，4399漫画网已经成为国内最具影响力的韩国漫画阅读平台之一。我们与多家韩国知名漫画工作室建立了合作关系，
                  确保用户能够第一时间阅读到最新、最全的韩国漫画内容。所有作品均为正版授权，拒绝删减，完整呈现原作精髓。
                </p>
                <p>
                  我们自主研发的下拉式阅读引擎，针对不同设备进行了深度优化。无论是PC端、平板还是手机，用户都能享受到流畅丝滑的
                  阅读体验。同时，我们的智能推荐系统会根据用户的阅读偏好，精准推荐最合适的漫画作品。
                </p>
                <p>
                  未来，4399漫画网将继续坚持品质至上、用户至上的理念，不断丰富漫画资源，优化阅读体验，让更多人爱上韩国漫画。
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* User Reviews */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              来自真实用户的评价，是我们前进的最大动力
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {reviews.map((review) => (
                <div key={review.id} className="p-5 rounded-xl bg-background-100 border border-background-200/60">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-background-200 flex-shrink-0">
                      <img src={review.avatar} alt={review.name} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.date}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i
                          key={i}
                          className={`text-xs ${i < review.rating ? "ri-star-fill text-accent-500" : "ri-star-line text-foreground-200"}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-background-100/70 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-8 text-center">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {[
                { q: "4399漫画网上的韩漫都是免费的吗？", a: "是的，4399漫画网上的所有韩国漫画资源均为免费阅读，无需付费即可畅享完整的韩漫内容。" },
                { q: "漫画更新频率是怎样的？", a: "我们与韩国漫画工作室保持同步更新，大部分连载作品每周至少更新一话，热门作品可能会更频繁。" },
                { q: "支持哪些设备阅读？", a: "4399漫画网全面适配PC、平板、手机等各类设备，采用响应式设计，在任何设备上都能获得流畅的阅读体验。" },
                { q: "如何搜索我想看的漫画？", a: "您可以通过网站顶部导航栏的搜索框输入漫画名称或关键词进行搜索，也可以通过分类页面按类型浏览。" },
                { q: "漫画内容有删减吗？", a: "我们坚持提供无删减版本，尊重原作的完整性。所有漫画内容均获得正版授权，完整呈现作者原意。" },
              ].map((faq, idx) => (
                <details key={idx} className="group bg-background-50 rounded-xl border border-background-200/60">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <span className="text-sm md:text-base font-medium text-foreground-800 pr-4">{faq.q}</span>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform duration-200 flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.a}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Form */}
        <section className="py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6 text-center">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">
              如有任何问题或建议，欢迎通过以下表单与我们取得联系
            </p>
            <div className="max-w-xl mx-auto">
              <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">您的姓名</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 text-foreground-700"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">电子邮箱</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 text-foreground-700"
                    placeholder="请输入您的电子邮箱"
                  />
                </div>
                <div>
                  <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    required
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 text-foreground-700"
                    placeholder="请输入主题"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    required
                    maxLength={500}
                    className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all duration-200 text-foreground-700 resize-none"
                    placeholder="请输入您的留言内容（最多500字）"
                  ></textarea>
                  <p className="text-xs text-foreground-400 mt-1">最多500字</p>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="spam-field"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? "提交中..." : "提交留言"}
                </button>

                {formStatus === "success" && (
                  <p className="text-sm text-center text-green-600 bg-green-50 py-2.5 rounded-lg">
                    留言提交成功！感谢您的反馈，我们会尽快回复。
                  </p>
                )}
                {formStatus === "error" && (
                  <p className="text-sm text-center text-red-600 bg-red-50 py-2.5 rounded-lg">
                    {formError || "提交失败，请稍后重试"}
                  </p>
                )}
              </form>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}